import { trans } from "@mongez/localization";
import { cartAtom, CustomOptionRequest } from "core/cart";
import { useAddToCart, useAddToCartQuantity } from "core/cart/hooks";
import { productAtom } from "core/catalog";
import { Product } from "core/catalog/types";
import { cn, parseError } from "core/shared";
import { userAtom } from "core/user";
import {
  Button,
  errorToast,
  Minus,
  Plus,
  successToast,
} from "~/design-system/components";

type ProductAddToCartProps = {
  product: Product;
  selectedCustomOptions: CustomOptionRequest[];
  isValid: boolean;
};

export function ProductAddToCart({
  product,
  selectedCustomOptions,
  isValid,
}: ProductAddToCartProps) {
  const { addToCart, loading } = useAddToCart({
    onSuccess: (data) => {
      successToast(trans("cart.addedToCart"));
      cartAtom.change("cart", data.cart);
      productAtom.change("inCart", true);
      userAtom.update(data.user);
    },
    onError: (response: any) => errorToast(parseError(response)),
  });

  const {
    quantity,
    incrementQuantity,
    decrementQuantity,
    minQuantity,
    maxQuantity,
  } = useAddToCartQuantity(product, product.purchase?.minQuantity || 1);

  if (!product.inStock) return null;

  return (
    <div className="flex w-full max-w-[800px] items-center gap-4 sm:flex-nowrap">
      <div className={cn("flex h-12 w-fit items-center rounded-full border")}>
        <Button
          aria-label={trans("decreaseQuantity")}
          title={trans("decreaseQuantity")}
          onClick={decrementQuantity}
          className="p-3 lg:px-4"
          disabled={
            quantity <= minQuantity && quantity !== product.cartItem?.quantity
          }
        >
          <Minus className="h-4 w-4" />
        </Button>

        <div className="bg-background text-primary flex min-w-12 items-center justify-center text-center text-xl">
          {quantity}
        </div>
        <Button
          aria-label={trans("increaseQuantity")}
          title={trans("increaseQuantity")}
          onClick={incrementQuantity}
          className="p-3 lg:p-4"
          disabled={quantity >= maxQuantity}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      <Button
        aria-label={trans("addToCart")}
        title={trans("addToCart")}
        className="hover:bg-primary-600 re w-full justify-center rounded-full bg-[#E8CFD3] py-3 text-black hover:text-white"
        onClick={() => {
          if (!isValid) {
            return errorToast(trans("pleaseFillAllRequiredFields"));
          }
          addToCart(product.id, quantity, selectedCustomOptions);
        }}
        disabled={loading}
      >
        {trans("addToCart")}
      </Button>
    </div>
  );
}
